import type { DnsRecordCreateRequest } from '@/types'

export interface DnsPreset {
  nameKey: string
  descriptionKey: string
  icon: string
  records: DnsRecordCreateRequest[]
}

export const DNS_PRESETS: Record<string, DnsPreset> = {
  googleWorkspace: {
    nameKey: 'dashboard.domains.dns.presets.googleWorkspace.name',
    descriptionKey: 'dashboard.domains.dns.presets.googleWorkspace.description',
    icon: 'mdi-google',
    records: [
      { type: 'MX', name: '', content: 'aspmx.l.google.com.', priority: 1, ttl: 3600 },
      { type: 'MX', name: '', content: 'alt1.aspmx.l.google.com.', priority: 5, ttl: 3600 },
      { type: 'MX', name: '', content: 'alt2.aspmx.l.google.com.', priority: 5, ttl: 3600 },
      { type: 'MX', name: '', content: 'alt3.aspmx.l.google.com.', priority: 10, ttl: 3600 },
      { type: 'MX', name: '', content: 'alt4.aspmx.l.google.com.', priority: 10, ttl: 3600 }
    ]
  },
  microsoft365: {
    nameKey: 'dashboard.domains.dns.presets.microsoft365.name',
    descriptionKey: 'dashboard.domains.dns.presets.microsoft365.description',
    icon: 'mdi-microsoft',
    records: [
      { type: 'MX', name: '', content: '{domain}.mail.protection.outlook.com.', priority: 0, ttl: 3600 }
    ]
  },
  spfGoogle: {
    nameKey: 'dashboard.domains.dns.presets.spfGoogle.name',
    descriptionKey: 'dashboard.domains.dns.presets.spfGoogle.description',
    icon: 'mdi-shield-check',
    records: [
      { type: 'TXT', name: '', content: 'v=spf1 include:_spf.google.com ~all', ttl: 3600 }
    ]
  },
  spfMicrosoft: {
    nameKey: 'dashboard.domains.dns.presets.spfMicrosoft.name',
    descriptionKey: 'dashboard.domains.dns.presets.spfMicrosoft.description',
    icon: 'mdi-shield-check',
    records: [
      { type: 'TXT', name: '', content: 'v=spf1 include:spf.protection.outlook.com ~all', ttl: 3600 }
    ]
  },
  dmarc: {
    nameKey: 'dashboard.domains.dns.presets.dmarc.name',
    descriptionKey: 'dashboard.domains.dns.presets.dmarc.description',
    icon: 'mdi-shield-lock',
    records: [
      { type: 'TXT', name: '_dmarc', content: 'v=DMARC1; p=quarantine; rua=mailto:dmarc@{domain}', ttl: 3600 }
    ]
  },
  webmail: {
    nameKey: 'dashboard.domains.dns.presets.webmail.name',
    descriptionKey: 'dashboard.domains.dns.presets.webmail.description',
    icon: 'mdi-email-outline',
    records: [
      { type: 'CNAME', name: 'mail', content: 'webmail.itsh.dev.', ttl: 3600 }
    ]
  }
}

// DNS Record Type Configuration
export interface DnsRecordTypeConfig {
  value: string
  label: string
  descriptionKey: string
  category: 'common' | 'mail' | 'security' | 'advanced'
  disabled?: boolean
  disabledReasonKey?: string
  placeholder?: string
  hintKey?: string
}

// All DNS record types supported by the API (26 types)
export const DNS_RECORD_TYPES: DnsRecordTypeConfig[] = [
  // Common types
  { value: 'A', label: 'A', descriptionKey: 'dashboard.domains.dns.recordTypes.A.description', category: 'common', placeholder: '192.168.1.1', hintKey: 'dashboard.domains.dns.recordTypes.A.hint' },
  { value: 'AAAA', label: 'AAAA', descriptionKey: 'dashboard.domains.dns.recordTypes.AAAA.description', category: 'common', placeholder: '2001:db8::1', hintKey: 'dashboard.domains.dns.recordTypes.AAAA.hint' },
  { value: 'CNAME', label: 'CNAME', descriptionKey: 'dashboard.domains.dns.recordTypes.CNAME.description', category: 'common', placeholder: 'www.example.com.', hintKey: 'dashboard.domains.dns.recordTypes.CNAME.hint' },
  { value: 'TXT', label: 'TXT', descriptionKey: 'dashboard.domains.dns.recordTypes.TXT.description', category: 'common', placeholder: 'v=spf1 include:_spf.google.com ~all', hintKey: 'dashboard.domains.dns.recordTypes.TXT.hint' },
  { value: 'ALIAS', label: 'ALIAS', descriptionKey: 'dashboard.domains.dns.recordTypes.ALIAS.description', category: 'common', placeholder: 'example.com.', hintKey: 'dashboard.domains.dns.recordTypes.ALIAS.hint' },
  { value: 'PTR', label: 'PTR', descriptionKey: 'dashboard.domains.dns.recordTypes.PTR.description', category: 'common', placeholder: 'host.example.com.', hintKey: 'dashboard.domains.dns.recordTypes.PTR.hint' },
  { value: 'NS', label: 'NS', descriptionKey: 'dashboard.domains.dns.recordTypes.NS.description', category: 'common', disabled: true, disabledReasonKey: 'dashboard.domains.dns.recordTypes.NS.disabledReason', placeholder: 'ns1.example.com.' },
  { value: 'SOA', label: 'SOA', descriptionKey: 'dashboard.domains.dns.recordTypes.SOA.description', category: 'common', disabled: true, disabledReasonKey: 'dashboard.domains.dns.recordTypes.SOA.disabledReason', placeholder: '' },

  // Mail types
  { value: 'MX', label: 'MX', descriptionKey: 'dashboard.domains.dns.recordTypes.MX.description', category: 'mail', placeholder: 'mail.example.com.', hintKey: 'dashboard.domains.dns.recordTypes.MX.hint' },
  { value: 'RP', label: 'RP', descriptionKey: 'dashboard.domains.dns.recordTypes.RP.description', category: 'mail', placeholder: 'admin.example.com. info.example.com.', hintKey: 'dashboard.domains.dns.recordTypes.RP.hint' },

  // Security types
  { value: 'CAA', label: 'CAA', descriptionKey: 'dashboard.domains.dns.recordTypes.CAA.description', category: 'security', placeholder: '0 issue "letsencrypt.org"', hintKey: 'dashboard.domains.dns.recordTypes.CAA.hint' },
  { value: 'TLSA', label: 'TLSA', descriptionKey: 'dashboard.domains.dns.recordTypes.TLSA.description', category: 'security', placeholder: '3 1 1 abc123...', hintKey: 'dashboard.domains.dns.recordTypes.TLSA.hint' },
  { value: 'SSHFP', label: 'SSHFP', descriptionKey: 'dashboard.domains.dns.recordTypes.SSHFP.description', category: 'security', placeholder: '2 1 abc123...', hintKey: 'dashboard.domains.dns.recordTypes.SSHFP.hint' },
  { value: 'SMIMEA', label: 'SMIMEA', descriptionKey: 'dashboard.domains.dns.recordTypes.SMIMEA.description', category: 'security', placeholder: '3 1 1 abc123...', hintKey: 'dashboard.domains.dns.recordTypes.SMIMEA.hint' },
  { value: 'OPENPGPKEY', label: 'OPENPGPKEY', descriptionKey: 'dashboard.domains.dns.recordTypes.OPENPGPKEY.description', category: 'security', placeholder: '(base64-encoded key)', hintKey: 'dashboard.domains.dns.recordTypes.OPENPGPKEY.hint' },
  { value: 'CERT', label: 'CERT', descriptionKey: 'dashboard.domains.dns.recordTypes.CERT.description', category: 'security', placeholder: '1 0 5 (base64)', hintKey: 'dashboard.domains.dns.recordTypes.CERT.hint' },

  // Advanced types
  { value: 'SRV', label: 'SRV', descriptionKey: 'dashboard.domains.dns.recordTypes.SRV.description', category: 'advanced', placeholder: 'sipserver.example.com.', hintKey: 'dashboard.domains.dns.recordTypes.SRV.hint' },
  { value: 'URI', label: 'URI', descriptionKey: 'dashboard.domains.dns.recordTypes.URI.description', category: 'advanced', placeholder: '"https://example.com/path"', hintKey: 'dashboard.domains.dns.recordTypes.URI.hint' },
  { value: 'HTTPS', label: 'HTTPS', descriptionKey: 'dashboard.domains.dns.recordTypes.HTTPS.description', category: 'advanced', placeholder: '1 . alpn=h3,h2', hintKey: 'dashboard.domains.dns.recordTypes.HTTPS.hint' },
  { value: 'SVCB', label: 'SVCB', descriptionKey: 'dashboard.domains.dns.recordTypes.SVCB.description', category: 'advanced', placeholder: '1 . alpn=h3', hintKey: 'dashboard.domains.dns.recordTypes.SVCB.hint' },
  { value: 'NAPTR', label: 'NAPTR', descriptionKey: 'dashboard.domains.dns.recordTypes.NAPTR.description', category: 'advanced', placeholder: '100 10 "u" "E2U+sip" "!^.*$!sip:info@example.com!" .', hintKey: 'dashboard.domains.dns.recordTypes.NAPTR.hint' },
  { value: 'AFSDB', label: 'AFSDB', descriptionKey: 'dashboard.domains.dns.recordTypes.AFSDB.description', category: 'advanced', placeholder: '1 afs.example.com.', hintKey: 'dashboard.domains.dns.recordTypes.AFSDB.hint' },
  { value: 'HINFO', label: 'HINFO', descriptionKey: 'dashboard.domains.dns.recordTypes.HINFO.description', category: 'advanced', placeholder: '"PC" "Linux"', hintKey: 'dashboard.domains.dns.recordTypes.HINFO.hint' },
  { value: 'IPSECKEY', label: 'IPSECKEY', descriptionKey: 'dashboard.domains.dns.recordTypes.IPSECKEY.description', category: 'advanced', placeholder: '10 1 2 192.0.2.1 AQNRU3...', hintKey: 'dashboard.domains.dns.recordTypes.IPSECKEY.hint' },
  { value: 'LOC', label: 'LOC', descriptionKey: 'dashboard.domains.dns.recordTypes.LOC.description', category: 'advanced', placeholder: '51 30 12.000 N 0 7 39.000 W 0.00m', hintKey: 'dashboard.domains.dns.recordTypes.LOC.hint' },
  { value: 'URL', label: 'URL', descriptionKey: 'dashboard.domains.dns.recordTypes.URL.description', category: 'advanced', placeholder: 'https://example.com', hintKey: 'dashboard.domains.dns.recordTypes.URL.hint' }
]

// Quick-select types for single record dialog
export const DNS_QUICK_TYPES = ['A', 'CNAME', 'MX', 'TXT'] as const

// Disabled types that cannot be created/edited
export const DNS_DISABLED_TYPES = ['NS', 'SOA'] as const

// Record types that require priority field
export const DNS_PRIORITY_TYPES = ['MX', 'SRV', 'URI', 'HTTPS', 'SVCB', 'NAPTR'] as const

// Record types that require weight field
export const DNS_WEIGHT_TYPES = ['SRV', 'URI'] as const

// Record types that require port field
export const DNS_PORT_TYPES = ['SRV'] as const

// Helper to get types by category
export const getDnsTypesByCategory = (category: DnsRecordTypeConfig['category']) =>
  DNS_RECORD_TYPES.filter((t) => t.category === category)

// Helper to get enabled types only
export const getEnabledDnsTypes = () => DNS_RECORD_TYPES.filter((t) => !t.disabled)

// TTL preset configuration
export interface DnsTtlPreset {
  value: number
  labelKey: string
}

// Common TTL presets
export const DNS_TTL_PRESETS: DnsTtlPreset[] = [
  { value: 300, labelKey: 'dashboard.domains.dns.ttlPresets.fiveMinutes' },
  { value: 900, labelKey: 'dashboard.domains.dns.ttlPresets.fifteenMinutes' },
  { value: 1800, labelKey: 'dashboard.domains.dns.ttlPresets.thirtyMinutes' },
  { value: 3600, labelKey: 'dashboard.domains.dns.ttlPresets.oneHour' },
  { value: 14400, labelKey: 'dashboard.domains.dns.ttlPresets.fourHours' },
  { value: 86400, labelKey: 'dashboard.domains.dns.ttlPresets.twentyFourHours' }
]

// Default TTL value
export const DNS_DEFAULT_TTL = 3600
